import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Form, 
  Button, 
  Card, 
  message,
  Steps,
  Row,
  Col,
  Space,
  Modal
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { CALENDAR_DATE_FORMAT } from '../../../settings';

// Redux Actions
import { 
  createOfflineBooking, 
  fetchOfflineBookings, 
  updateOfflineBooking, 
  deleteOfflineBooking,
  fetchAllSpaces,
  searchSpaces,
  fetchSpaceById,
  fetchSpaceBookings,
  getCalendarData,
  clearOfflineBookingError,
  resetOfflineBookingForm,
  clearSpaceDetails,
  createPaymentForOfflineBooking,
  fetchPaymentForOfflineBooking,
  updatePaymentStatusForOfflineBooking
} from './OfflineBookingActions';

// Subcomponents
import BookingDetailsForm from './components/BookingDetailsForm';
import ClientDetailsForm from './components/ClientDetailsForm';
import FinancialDetailsForm from './components/FinancialDetailsForm';
import PaymentDetailsForm from './components/PaymentDetailsForm';
import SpaceInfoCard from './components/SpaceInfoCard';
import CalendarView from './components/CalendarView';

// Custom Hooks
import { useBookingCalculations } from './hooks/useBookingCalculations';
import { useChargingType } from './hooks/useChargingType';
import { useCalendarAvailability } from './hooks/useCalendarAvailability';

// Icons
import { PlusOutlined, CalendarOutlined, UnorderedListOutlined } from '@ant-design/icons';

// Styles
import '../../../assets/scss/components/OfflineBooking/OfflineBooking.scss';

// Constants for discount/saving IDs from backend
const DISCOUNT_IDS = {
  MS_DISCOUNT: '5e7cb0aeaac4f142d8075f85',  // Deal/MS Discount
  HOST_DISCOUNT: '5e7cb0aeaac4f142d8075f86' // Host Discount
};

const OfflineBooking = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const history = useHistory();
  
  // Redux State
  const {
    bookings = [],
    spaces = [],
    spaceDetails,
    spaceBookings = [],
    calendarData = {},
    loading,
    loadingSpaces,
    loadingSpaceDetails,
    loadingBookings,
    error,
    success
  } = useSelector(state => state.offlineBooking || {});
  
  // Local State
  const [currentStep, setCurrentStep] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedSpace, setSelectedSpace] = useState(null);
  
  // Memoize spaces list to prevent unnecessary re-renders of dropdown
  const memoizedSpaces = useMemo(() => spaces, [spaces.length]);
  
  // Custom Hooks
  const {
    chargingType,
    selectedChargingOption,
    availableBlocks,
    availableChargingTypes,
    handleChargingTypeChange
  } = useChargingType(form, spaceDetails);
  
  const {
    calculatedValues,
    handleFormValuesChange
  } = useBookingCalculations(form, selectedChargingOption, spaceDetails);
  
  const {
    availableSlots,
    bookedSlots
  } = useCalendarAvailability(spaceDetails, spaceBookings, selectedDate);

  // Sync billing date to payment schedule date
  useEffect(() => {
    const billingDate = form.getFieldValue('billingDate');
    if (billingDate && !editMode) {
      form.setFieldsValue({
        paymentScheduleOn: billingDate
      });
    }
  }, [form.getFieldValue('billingDate')]);

  // Mounted ref to avoid state updates after unmount
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { 
      isMountedRef.current = false; 
    };
  }, []);

  // Cleanup form and state when component unmounts (navigating away)
  useEffect(() => {
    return () => {
      // Reset form fields
      form.resetFields();
      
      // Clear Redux state
      dispatch(resetOfflineBookingForm());
      dispatch(clearSpaceDetails());
    };
  }, [form, dispatch]);
  
  // Load Initial Data - Only load once on mount
  useEffect(() => {
    // Only fetch spaces if not already loaded
    if (!spaces || spaces.length === 0) {
      dispatch(fetchAllSpaces(1, 20, '', 'LK', false));
    }
    dispatch(fetchOfflineBookings());
  }, []); // Empty dependency array - only run once on mount

  // Handle Edit Mode from Navigation State
  useEffect(() => {
    const locationState = history.location?.state;
    
    if (locationState && locationState.editMode && locationState.editRecord) {
      const record = locationState.editRecord;
      console.log('Edit mode activated for booking:', record);
      
      setEditMode(true);
      setEditingId(record.id || record._id);
      
      const spaceId = record.space || record.spaceId;
      setSelectedSpace(spaceId);
      
      // Extract pricing options for times
      const pricingOption = record.pricingOptions && record.pricingOptions[0];
      const startTimeStr = pricingOption?.startTime;
      const endTimeStr = pricingOption?.endTime;
      
      // Convert time strings to moment objects (e.g., "9:00 am" -> moment)
      let bookingStartTime = null;
      let bookingEndTime = null;
      
      if (startTimeStr && startTimeStr !== '24/7 Access') {
        bookingStartTime = moment(startTimeStr, ['h:mm a', 'HH:mm']);
        // Trigger calculation after auto-filling values to fix initial display
        setTimeout(() => {
          handleFormValuesChange();
        }, 100);
      }
      
      if (endTimeStr && endTimeStr !== '24/7 Access') {
        bookingEndTime = moment(endTimeStr, ['h:mm a', 'HH:mm']);
      }
      
      // Populate form with booking data
      const formData = {
        // Space selection - use 'space' field name to match the form
        space: spaceId,
        selectedChargingType: record.chargingType || 'daily',
        
        // Booking reference
        bookingRef: record.bookingRef || record.id || record._id,
        
        // Billing date - use current date or booking creation date
        billingDate: record.createdAt ? moment(record.createdAt) : moment(),
        
        // Client details
        clientName: record.user?.name || record.clientName || '',
        clientEmail: record.user?.email || record.clientEmail || '',
        clientPhone: record.user?.contactNumber || '',
        
        // Booking details
        numberOfGuests: record.guest || record.numberOfGuests || 1,
        guestCount: record.guest || record.numberOfGuests || 1,
        eventType: record.eventType || '',
        
        // Start and End Times (for hourly bookings)
        bookingStartTime: bookingStartTime,
        bookingEndTime: bookingEndTime,
        
        // Financial details - load from financialDetails if available, otherwise use defaults
        bookingValue: record.financialDetails?.baseBookingValue || record.totalPrice || 0,
        hostDiscountPercent: record.financialDetails?.hostDiscountPercent || 0,
        msDiscountPercent: record.financialDetails?.msDiscountPercent || 0,
        taxAmount: record.financialDetails?.taxAmount || record.financialDetails?.taxValue || 0,
        commissionPercent: record.financialDetails?.commissionPercent || 0,
        ccChargePercent: record.financialDetails?.ccChargePercent || 0,
        currency: record.currency || 'LKR',
        
        // NEW: Load price adjustment data if present
        priceAdjustmentReason: record.financialDetails?.priceAdjustmentReason || '',
        
        // Payment details - DO NOT populate in edit mode
        // Payment is managed separately through Payment Management Modal
        // These fields should only be populated when creating new bookings
        
        // Dates - handle moment conversion
        dailyStartDate: record.bookingStartDate ? moment(record.bookingStartDate) : null,
        dailyEndDate: record.bookingEndDate ? moment(record.bookingEndDate) : null,
        weeklyStartDate: record.bookingStartDate ? moment(record.bookingStartDate) : null,
        monthlyStartDate: record.bookingStartDate ? moment(record.bookingStartDate) : null,
        hourlyBookingDate: record.bookingStartDate ? moment(record.bookingStartDate) : null,
        blockBookingDate: record.bookingStartDate ? moment(record.bookingStartDate) : null,
        
        numberOfDays: record.duration || 1,
        numberOfWeeks: record.duration || 1,
        numberOfMonths: record.duration || 1,
        hourlyDuration: record.duration || 1,
      };
      
      console.log('Setting form values:', formData);
      console.log('Financial details from record:', record.financialDetails);
      
      // Set form values
      form.setFieldsValue(formData);
      
      // Fetch payment data for edit mode to populate payment fields
      const bookingId = record.id || record._id;
      if (bookingId) {
        console.log('Fetching payment data for booking:', bookingId);
        dispatch(fetchPaymentForOfflineBooking(bookingId)).then((response) => {
          if (response && response.success && response.payload) {
            const paymentData = response.payload;
            console.log('Payment data fetched:', paymentData);
            
            // Populate payment fields from fetched payment data
            form.setFieldsValue({
              paymentAmount: paymentData.amount || 0,
              paymentMethod: paymentData.paymentMethod || paymentData.paymentType || 'BANK_TRANSFER',
              paymentStatus: paymentData.paymentStatus || 'INITIATED',
              paymentScheduleOn: paymentData.paymentScheduleOn ? moment(paymentData.paymentScheduleOn) : moment(),
              paymentDescription: paymentData.paymentDescription || '',
              transactionReference: paymentData.transactionReference || paymentData.transactionRef || ''
            });
          } else {
            console.log('No payment data found for this booking');
          }
        }).catch((error) => {
          console.log('Could not fetch payment data:', error);
        });
      }
      
      // Trigger calculation after setting form values in edit mode
      // This ensures calculated fields display properly
      setTimeout(() => {
        handleFormValuesChange();
      }, 100);
      
      // Load space details for the selected space
      if (spaceId) {
        console.log('Loading space details for:', spaceId);
        dispatch(fetchSpaceById(spaceId));
      }
      
      // Clear navigation state to prevent re-triggering
      history.replace('/dashboard/booking/offline', {});
    }
  }, [history, dispatch, form]);

  // Separate useEffect to set seating arrangement after space details are loaded in edit mode
  useEffect(() => {
    if (editMode && editingId && spaceDetails) {
      const locationState = history.location?.state;
      const record = locationState?.editRecord;
      
      if (record?.seatArrangement?.name) {
        const arrangements = spaceDetails?.events?.[0]?.seatArrangements || [];
        const matchedArrangement = arrangements.find(arr => 
          arr.seatArrangement?.name === record.seatArrangement.name
        );
        
        if (matchedArrangement) {
          form.setFieldsValue({
            seatingArrangement: matchedArrangement.seatArrangement.id
          });
          console.log('Set seating arrangement in edit mode:', matchedArrangement.seatArrangement.name);
        }
      }
    }
  }, [editMode, editingId, spaceDetails, form, history]);

  // Auto-fill discount and commission values from space data (only in create mode)
  useEffect(() => {
    if (spaceDetails && !editMode) {
      // Only auto-load from space if NOT in edit mode
      // In edit mode, values are loaded from saved booking in the edit useEffect
      const currentValues = form.getFieldsValue([
        'hostDiscountPercent',
        'msDiscountPercent',
        'commissionPercent',
        'ccChargePercent',
        'taxAmount'
      ]);
      
      // Only set if fields are empty/undefined (first time loading)
      const shouldAutoFill = 
        currentValues.hostDiscountPercent === undefined &&
        currentValues.commissionPercent === undefined;
      
      if (shouldAutoFill) {
        // Try to find the selected event type
        const selectedEventType = form.getFieldValue('eventType');
        const selectedChargingTypeValue = form.getFieldValue('selectedChargingType');
        
        // Check both events and direct properties
        let eventData = null;
        if (spaceDetails.events && spaceDetails.events.length > 0) {
          eventData = spaceDetails.events[0];
          if (selectedEventType) {
            const matchingEvent = spaceDetails.events.find(e => 
              e.eventType?.name === selectedEventType || e.eventType?.id === selectedEventType
            );
            if (matchingEvent) {
              eventData = matchingEvent;
            }
          }
        }
        
        // Initialize default values
        let hostDiscountPercent = 0;
        let msDiscountPercent = 0;
        let commissionPercent = 0;
        let ccChargePercent = 0;
        let taxValue = 0;
        
        // First priority: Get commission from payout
        if (spaceDetails.payout && spaceDetails.payout.commission) {
          commissionPercent = parseFloat(spaceDetails.payout.commission) || 0;
        }
        
        // Check for chargings array in event (Meet/Train/Shoot spaces have this structure)
        if (eventData && eventData.chargings && Array.isArray(eventData.chargings)) {
          // Find charging matching selected type - compare by ID or name
          let matchingCharging = null;
          
          if (selectedChargingTypeValue) {
            matchingCharging = eventData.chargings.find(ch => 
              ch.chargingType?.id === selectedChargingTypeValue ||
              ch.chargingType?.name?.toLowerCase() === selectedChargingTypeValue.toLowerCase()
            );
          }
          
          // If no match, use first charging as default
          if (!matchingCharging && eventData.chargings.length > 0) {
            matchingCharging = eventData.chargings[0];
          }
          
          // Check for discounts in charging - these are the host/MS discounts
          if (matchingCharging && matchingCharging.discounts && Array.isArray(matchingCharging.discounts)) {
            matchingCharging.discounts.forEach(discount => {
              // Match by discount ID using constants
              if (discount.id === DISCOUNT_IDS.MS_DISCOUNT) {
                msDiscountPercent = parseFloat(discount.amount) || 0;
              } else if (discount.id === DISCOUNT_IDS.HOST_DISCOUNT) {
                hostDiscountPercent = parseFloat(discount.amount) || 0;
              }
            });
          }
        }
        
        // Check for spaceSavings (alternative structure for Work spaces)
        if (eventData && eventData.spaceSavings && Array.isArray(eventData.spaceSavings)) {
          const matchingSavings = eventData.spaceSavings.find(ss => {
            const chargingType = ss.chargingType?.toLowerCase() || '';
            return chargingType === (selectedChargingTypeValue || '').toLowerCase();
          });
          
          if (matchingSavings && matchingSavings.savings && Array.isArray(matchingSavings.savings)) {
            matchingSavings.savings.forEach(savingItem => {
              const saving = savingItem.saving;
              if (saving && saving.name) {
                if (saving.name === 'Host Discount' && saving.amountType === 'PERCENTAGE') {
                  hostDiscountPercent = parseFloat(savingItem.amount) || 0;
                } else if (saving.name === 'Deal' && saving.amountType === 'PERCENTAGE') {
                  msDiscountPercent = parseFloat(savingItem.amount) || 0;
                }
              }
            });
          }
        }
        
        // Fallback: Check direct properties on event
        if (hostDiscountPercent === 0 && eventData) {
          hostDiscountPercent = parseFloat(eventData.hostDiscount || eventData.hostDiscountPercent || 0);
        }
        if (msDiscountPercent === 0 && eventData) {
          msDiscountPercent = parseFloat(eventData.msDiscount || eventData.msDiscountPercent || 0);
        }
        if (commissionPercent === 0 && eventData) {
          commissionPercent = parseFloat(eventData.msCommission || eventData.commissionPercent || 0);
        }
        
        // CC Charge
        if (eventData) {
          ccChargePercent = parseFloat(eventData.ccCharge || eventData.ccChargePercent || 0);
        }
        
        // Calculate tax from applicableTaxes
        // Note: applicableTaxes often only contains IDs, not full objects
        // Leave at 0 if only IDs present - backend will calculate the correct tax
        if (spaceDetails.applicableTaxes && Array.isArray(spaceDetails.applicableTaxes) && spaceDetails.applicableTaxes.length > 0) {
          const hasRateInfo = spaceDetails.applicableTaxes.some(tax => 
            tax.rate || tax.percentage || tax.value || tax.amount
          );
          
          if (hasRateInfo) {
            taxValue = spaceDetails.applicableTaxes.reduce((sum, tax) => {
              const rate = parseFloat(
                tax.rate || 
                tax.percentage || 
                tax.value || 
                tax.amount ||
                0
              );
              return sum + rate;
            }, 0);
          }
        } else if (eventData) {
          // Fallback to event tax
          taxValue = parseFloat(eventData.tax || eventData.taxValue || eventData.taxPercent || 0);
        }

        form.setFieldsValue({
          hostDiscountPercent: hostDiscountPercent || 0,
          msDiscountPercent: msDiscountPercent || 0,
          commissionPercent: commissionPercent || 0,
          ccChargePercent: ccChargePercent || 0,
          taxAmount: taxValue || 0
        });
        
        // Also set start/end times if not set (to ensure calculation uses correct initial values)
        if (!form.getFieldValue('bookingStartTime') || !form.getFieldValue('bookingEndTime')) {
          const today = moment();
          const pricingStart = selectedChargingOption?.pricingOptions?.[0]?.startTime;
          const parseTime = (t) => t ? moment(t, ['h:mm a','HH:mm','h:mm A','H:mm']) : null;
          const startTimeMoment = parseTime(pricingStart) || moment('09:00','HH:mm');
          const start = moment(today).hour(startTimeMoment.hour()).minute(startTimeMoment.minute());
          const end = moment(start).add(1, 'hour');
          form.setFieldsValue({
            bookingStartTime: start,
            bookingEndTime: end
          });
        }
        
        // Wait for all fields to update, then trigger calculation
        setTimeout(() => {
          handleFormValuesChange();
        }, 100);
      }
    }
  }, [spaceDetails, form, editMode, selectedChargingOption, handleFormValuesChange]);
  
  // Handle Form Submit
  const onFinish = async (values) => {
    try {
      console.log('Form values:', values);
      console.log('Space details:', spaceDetails);
      console.log('Charging option:', selectedChargingOption);
      console.log('Calculated values:', calculatedValues);
      
      // Get all form values including conditionally rendered fields
      const allFormValues = form.getFieldsValue();
      console.log('All form values:', allFormValues);
      
      // Validate required fields
      if (!selectedSpace) {
        message.error('Please select a space');
        return;
      }
      
      if (!spaceDetails) {
        message.error('Space details not loaded. Please reselect the space.');
        return;
      }
      
      // NEW: Validate price adjustment reason if price was manually adjusted
      if (calculatedValues.isPriceAdjusted) {
        // Get the reason directly from form to ensure we capture conditionally rendered fields
        const reason = (values.priceAdjustmentReason || form.getFieldValue('priceAdjustmentReason') || '').trim();
        console.log('Price adjustment validation:', {
          fromValues: values.priceAdjustmentReason,
          fromForm: form.getFieldValue('priceAdjustmentReason'),
          finalReason: reason,
          length: reason.length,
          isPriceAdjusted: calculatedValues.isPriceAdjusted
        }); // Debug log
        
        if (!reason || reason.length < 10) {
          message.error('Please provide a valid reason for manual price adjustment (minimum 10 characters) before submitting');
          setCurrentStep(2); // Go to financial details step
          return;
        }
        
        // Validate adjustment is within reasonable bounds (-90% to +50%)
        const adjustmentPercent = calculatedValues.priceAdjustmentPercent;
        if (adjustmentPercent < -90 || adjustmentPercent > 50) {
          Modal.confirm({
            title: 'Extreme Price Adjustment Detected',
            content: `The price adjustment of ${adjustmentPercent.toFixed(2)}% is outside the typical range (-90% to +50%). Are you sure you want to proceed?`,
            okText: 'Yes, Continue',
            cancelText: 'Go Back',
            onOk: () => {
              // Continue with submission
              proceedWithSubmission(values);
            }
          });
          return; // Exit here, will continue in onOk callback
        }
        
        // Show confirmation modal for manual adjustment
        Modal.confirm({
          title: 'Confirm Manual Price Adjustment',
          content: (
            <div>
              <p><strong>You are manually adjusting the booking price:</strong></p>
              <p>System Calculated Price: <strong>{calculatedValues.originalCalculatedPrice} {values.currency || 'LKR'}</strong></p>
              <p>Adjusted Price: <strong>{values.bookingValue || form.getFieldValue('bookingValue')} {values.currency || 'LKR'}</strong></p>
              <p>Adjustment: <strong style={{color: calculatedValues.priceAdjustmentAmount >= 0 ? 'green' : 'red'}}>
                {calculatedValues.priceAdjustmentAmount >= 0 ? '+' : ''}{calculatedValues.priceAdjustmentAmount} {values.currency || 'LKR'} 
                ({calculatedValues.priceAdjustmentPercent >= 0 ? '+' : ''}{calculatedValues.priceAdjustmentPercent.toFixed(2)}%)
              </strong></p>
              <p>Reason: <em>{reason}</em></p>
              <br/>
              <p><strong>This will override system calculations for receipts, emails, and confirmations.</strong></p>
              <p>Do you want to continue?</p>
            </div>
          ),
          okText: 'Yes, Submit with Adjusted Price',
          cancelText: 'Cancel',
          okButtonProps: { danger: true },
          onOk: () => {
            // Continue with submission
            proceedWithSubmission(values);
          }
        });
        return; // Exit here, will continue in onOk callback
      }
      
      // If no adjustment, proceed directly
      await proceedWithSubmission(values);
      
    } catch (err) {
      console.error('Error in onFinish:', err);
      message.error(err.message || 'Failed to save booking');
    }
  };
  
  // Extracted submission logic to be called from onFinish or confirmation modal
  const proceedWithSubmission = async (values) => {
    try {
      
      // Validate client details - check form fields directly
      const clientName = values.clientName || form.getFieldValue('clientName');
      const clientEmail = values.clientEmail || form.getFieldValue('clientEmail') || '';
      const clientPhone = values.clientPhone || form.getFieldValue('clientPhone') || '';
      
      console.log('Client details:', { clientName, clientEmail, clientPhone }); // Debug log
      
      if (!clientName) {
        message.error('Client name is required. Please go back to Step 2 (Client Info).');
        setCurrentStep(1); // Go back to client details step
        return;
      }
      
      // Show info message if email is not provided
      if (!clientEmail || !clientEmail.trim()) {
        console.log('No client email provided - booking confirmation emails will not be sent');
      }
      
      // Determine start and end dates based on charging type
      let bookingStartDate, bookingEndDate, duration = 0;
      let pricingOptions = [];
      const chargingTypeValue = selectedChargingOption?.type || values.selectedChargingType || 'daily';
      
      if (chargingTypeValue === 'hourly') {
        // Get values from form state to ensure we have the data
        const bookingDate = values.hourlyBookingDate || form.getFieldValue('hourlyBookingDate');
        const startTime = values.bookingStartTime || form.getFieldValue('bookingStartTime');
        const endTime = values.bookingEndTime || form.getFieldValue('bookingEndTime');
        
        if (!bookingDate || !startTime || !endTime) {
          message.error('Please select booking date and times for hourly booking');
          setCurrentStep(0);
          return;
        }

        // Validate time order
        try {
          const start = moment(startTime, ['h:mm a', 'HH:mm']).isValid() && startTime.format ? startTime : moment(startTime);
          const end = moment(endTime, ['h:mm a', 'HH:mm']).isValid() && endTime.format ? endTime : moment(endTime);
          if (!start || !end || !start.isValid() || !end.isValid() || end.isSameOrBefore(start)) {
            message.error('End time must be after start time');
            setCurrentStep(0);
            return;
          }
        } catch (e) {
          message.error('Invalid start/end time');
          setCurrentStep(0);
          return;
        }
        
        // Combine date with time for proper datetime
        // Format dates as YYYY-MM-DD for clean midnight parsing (no time component)
        bookingStartDate = moment(bookingDate).format('YYYY-MM-DD');
        bookingEndDate = moment(bookingDate).format('YYYY-MM-DD');
        duration = values.hourlyDuration || form.getFieldValue('hourlyDuration') || 1;
        
        // Build pricing options array for hourly booking (matches SYSTEM_BOOKING format)
        pricingOptions = [{
          name: 'Hourly',
          startTime: (startTime?.format ? startTime.format('h:mm a') : moment(startTime).format('h:mm a')).toLowerCase(),
          endTime: (endTime?.format ? endTime.format('h:mm a') : moment(endTime).format('h:mm a')).toLowerCase(),
          price: {
            amount: selectedChargingOption?.hourlyRate || calculatedValues?.finalBookingValue || parseFloat(values.bookingValue) || 0,
            currency: values.currency || 'LKR',
            currencySymbol: values.currency || 'LKR'
          }
        }];
      } else if (chargingTypeValue === 'daily') {
        // Get values from form state to ensure we have the data
        const startDate = values.dailyStartDate || form.getFieldValue('dailyStartDate');
        const endDate = values.dailyEndDate || form.getFieldValue('dailyEndDate');
        
        if (!startDate || !endDate) {
          message.error('Please select start and end dates for daily booking');
          setCurrentStep(0);
          return;
        }
        
        // Format dates as YYYY-MM-DD for clean midnight parsing (no time component)
        bookingStartDate = moment(startDate).format('YYYY-MM-DD');
        bookingEndDate = moment(endDate).format('YYYY-MM-DD');
        duration = values.numberOfDays || form.getFieldValue('numberOfDays') || 1;
        
        // Build pricing options for daily booking with start/end times from operational hours
        const dailyStartTime = selectedChargingOption?.pricingOptions?.[0]?.startTime || '9:00 am';
        const dailyEndTime = selectedChargingOption?.pricingOptions?.[0]?.endTime || '5:00 pm';
        
        pricingOptions = [{
          name: 'Daily',
          startTime: dailyStartTime.toLowerCase(),
          endTime: dailyEndTime.toLowerCase(),
          price: {
            amount: selectedChargingOption?.dailyRate || calculatedValues?.finalBookingValue || parseFloat(values.bookingValue) || 0,
            currency: values.currency || 'LKR',
            currencySymbol: values.currency || 'LKR'
          }
        }];
      } else if (chargingTypeValue === 'block') {
        const selectedBlock = availableBlocks?.find(b => 
          (b.id || availableBlocks.indexOf(b)) === values.selectedBlock
        );
        // Get block date from form state
        const blockDate = values.blockBookingDate || form.getFieldValue('blockBookingDate');
        
        if (!blockDate) {
          message.error('Please select booking date for block booking');
          setCurrentStep(0);
          return;
        }
        
        // Format dates as YYYY-MM-DD for clean midnight parsing (no time component)
        bookingStartDate = moment(blockDate).format('YYYY-MM-DD');
        bookingEndDate = moment(blockDate).format('YYYY-MM-DD');
        duration = 1;
        
        // Build pricing options for block booking - use block times or fall back to operational hours
        const blockStartTime = selectedBlock?.startTime || selectedChargingOption?.pricingOptions?.[0]?.startTime || '9:00 am';
        const blockEndTime = selectedBlock?.endTime || selectedChargingOption?.pricingOptions?.[0]?.endTime || '5:00 pm';
        
        pricingOptions = [{
          name: selectedBlock?.name || 'Block',
          startTime: blockStartTime.toLowerCase(),
          endTime: blockEndTime.toLowerCase(),
          price: {
            amount: selectedBlock?.price || selectedChargingOption?.blockRate || calculatedValues?.finalBookingValue || parseFloat(values.bookingValue) || 0,
            currency: values.currency || 'LKR',
            currencySymbol: values.currency || 'LKR'
          }
        }];
      } else if (chargingTypeValue === 'weekly') {
        // Get weekly start date from form state
        const startDate = values.weeklyStartDate || form.getFieldValue('weeklyStartDate');
        const numberOfWeeks = values.numberOfWeeks || form.getFieldValue('numberOfWeeks') || 1;
        
        if (!startDate) {
          message.error('Please select start date for weekly booking');
          setCurrentStep(0);
          return;
        }
        
        // Format dates as YYYY-MM-DD for clean midnight parsing (no time component)
        bookingStartDate = moment(startDate).format('YYYY-MM-DD');
        bookingEndDate = moment(startDate).add(numberOfWeeks, 'weeks').format('YYYY-MM-DD');
        duration = numberOfWeeks;
        
        // Build pricing options for weekly booking with operational hours
        const weeklyStartTime = selectedChargingOption?.pricingOptions?.[0]?.startTime || '9:00 am';
        const weeklyEndTime = selectedChargingOption?.pricingOptions?.[0]?.endTime || '5:00 pm';
        
        pricingOptions = [{
          name: 'Weekly',
          startTime: weeklyStartTime.toLowerCase(),
          endTime: weeklyEndTime.toLowerCase(),
          price: {
            amount: selectedChargingOption?.weeklyRate || calculatedValues?.basePrice || 0,
            currency: values.currency || 'LKR',
            currencySymbol: values.currency || 'LKR'
          }
        }];
      } else if (chargingTypeValue === 'monthly') {
        // Get monthly start date from form state
        const startDate = values.monthlyStartDate || form.getFieldValue('monthlyStartDate');
        const numberOfMonths = values.numberOfMonths || form.getFieldValue('numberOfMonths') || 1;
        
        if (!startDate) {
          message.error('Please select start date for monthly booking');
          setCurrentStep(0);
          return;
        }
        
        // Format dates as YYYY-MM-DD for clean midnight parsing (no time component)
        bookingStartDate = moment(startDate).format('YYYY-MM-DD');
        bookingEndDate = moment(startDate).add(numberOfMonths, 'months').format('YYYY-MM-DD');
        duration = numberOfMonths;
        
        // Build pricing options for monthly booking with operational hours
        const monthlyStartTime = selectedChargingOption?.pricingOptions?.[0]?.startTime || '9:00 am';
        const monthlyEndTime = selectedChargingOption?.pricingOptions?.[0]?.endTime || '5:00 pm';
        
        pricingOptions = [{
          name: 'Monthly',
          startTime: monthlyStartTime.toLowerCase(),
          endTime: monthlyEndTime.toLowerCase(),
          price: {
            amount: selectedChargingOption?.monthlyRate || calculatedValues?.basePrice || 0,
            currency: values.currency || 'LKR',
            currencySymbol: values.currency || 'LKR'
          }
        }];
      } else {
        // Format dates as YYYY-MM-DD for clean midnight parsing (no time component)
        bookingStartDate = moment(values.startDate || new Date()).format('YYYY-MM-DD');
        bookingEndDate = moment(values.endDate || new Date()).format('YYYY-MM-DD');
        
        // Default pricing option (matches SYSTEM_BOOKING format)
        pricingOptions = [{
          name: 'Daily',
          startTime: '9:00 am',
          endTime: '5:00 pm',
          price: {
            amount: calculatedValues?.basePrice || 0,
            currency: values.currency || 'LKR',
            currencySymbol: values.currency || 'LKR'
          }
        }];
      }
      
      // Calculate total price correctly from financial details
      const totalPrice = calculatedValues?.finalBookingValue 
        ? parseFloat(calculatedValues.finalBookingValue) 
        : (values.totalPrice || 0);
      
      // Build complete booking data like SYSTEM_BOOKING (with ALL required fields)
      const bookingData = {
        // Core Booking Details
        space: selectedSpace || values.space,
        bookingSpace: spaceDetails?.id || spaceDetails?._id,
        bookingRef: values.bookingRef,
        title: spaceDetails?.spaceName || spaceDetails?.name || 'Offline Booking',
        description: values.description || '',
        
        // Booking Dates & Times (REQUIRED - matching SYSTEM_BOOKING)
        bookingStartDate: bookingStartDate,
        bookingEndDate: bookingEndDate,
        bookingStartDateTime: bookingStartDate, // Will be properly calculated by backend
        bookingEndDateTime: bookingEndDate,     // Will be properly calculated by backend
        duration: duration,
        billingDate: values.billingDate?.toDate?.() || values.billingDate,
        
        // Space & Event Info
        spaceCategory: spaceDetails?.events?.[0]?.eventType?.spaceCategory?.name || '',
        eventType: spaceDetails?.events?.[0]?.eventType?.name || '',
        eventTypeId: spaceDetails?.events?.[0]?.eventType?.id || '',
        
        // Location & Timezone (REQUIRED - matching SYSTEM_BOOKING)
        location: spaceDetails?.location ? {
          latitude: spaceDetails.location.geoLocation?.latitude || spaceDetails.location.latitude,
          longitude: spaceDetails.location.geoLocation?.longitude || spaceDetails.location.longitude,
          address: spaceDetails.location.address || '',
          country: spaceDetails.location.country || 'LK'
        } : undefined,
        timezone: spaceDetails?.location?.timezone || 'Asia/Colombo',
        
        // Host Information (REQUIRED - matching SYSTEM_BOOKING)
        host: spaceDetails?.spaceOwner?.email || spaceDetails?.host?.email || spaceDetails?.ownerEmail,
        
        // Charging Details
        chargingType: chargingTypeValue.charAt(0).toUpperCase() + chargingTypeValue.slice(1),
        
        // CRITICAL: Pricing Options Array with name, times, and price (matching SYSTEM_BOOKING)
        pricingOptions: pricingOptions,
        
        // Seat Arrangement (if applicable for Meet/Train spaces)
        seatArrangement: (() => {
          // Check both field names: seatingArrangement (form field) and seatArrangement (legacy)
          // Also check form directly to ensure we get the value
          const seatArrangementId = values.seatingArrangement || values.seatArrangement || form.getFieldValue('seatingArrangement');
          if (!seatArrangementId) return undefined;
          
          // Find the selected arrangement from space details
          const arrangements = spaceDetails?.events?.[0]?.seatArrangements || [];
          const selectedArrangement = arrangements.find(arr => 
            arr.seatArrangement?.id === seatArrangementId
          );
          
          if (selectedArrangement) {
            return {
              name: selectedArrangement.seatArrangement.name,
              icon: selectedArrangement.seatArrangement.icon || ''
            };
          }
          
          return undefined;
        })(),
        
        // Guest Count - use guestCount from form (field name is guestCount, not numberOfGuests)
        guest: values.guestCount || values.numberOfGuests || form.getFieldValue('guestCount') || 1,
        
        // User/Client Details: support existing users (selected) or manual entry
        // Email is optional - if not provided, booking emails will not be sent
        user: {
          id: values.clientUserId || form.getFieldValue('clientUserId') || undefined,
          name: clientName,
          email: clientEmail && clientEmail.trim() ? clientEmail.trim() : undefined,
          contactNumber: clientPhone
        },
        
        // Financial Details - CORRECTED CALCULATION
        totalPrice: totalPrice,
        currency: values.currency || 'LKR',
        
        // Booking Type & Status
        bookingType: 'OFFLINE_BOOKING',
        // Set booking status based on payment method and status
        bookingStatus: (() => {
          const paymentMethod = values.paymentMethod || 'BANK_TRANSFER';
          const paymentStatus = values.paymentStatus || 'INITIATED';
          
          // If payment is completed, booking is confirmed
          if (paymentStatus === 'COMPLETED') {
            return 'CONFIRMED';
          }
          
          // For bank transfer or pending payments, status is TENTATIVE
          if (paymentMethod === 'BANK_TRANSFER' || paymentStatus === 'INITIATED') {
            return 'TENTATIVE';
          }
          
          // For cash/card with completed payment
          if (paymentStatus === 'COMPLETED') {
            return 'CONFIRMED';
          }
          
          // Default to TENTATIVE for offline bookings awaiting payment confirmation
          return 'TENTATIVE';
        })(),
        
  // CRITICAL: Payment Type - map UI methods to backend enums
  // Backend accepts: CARD, BANK_TRANSFER, PROMO_CODE
  paymentType: (() => {
    switch (values.paymentMethod) {
      case 'CARD': return 'CARD';
      case 'CASH': return 'CASH';
      case 'BANK_TRANSFER': return 'BANK_TRANSFER';
      case 'CHEQUE': return 'CHEQUE';
      case 'OTHER': return 'OTHER';
      default: return 'BANK_TRANSFER';
    }
  })(),
        
        // Payment Info for manual payment recording
        paymentMethod: values.paymentMethod,
        paymentDescription: values.paymentDescription,
        paymentStatus: values.paymentStatus || 'INITIATED',
        paymentScheduleOn: values.paymentScheduleOn?.toDate?.() || values.paymentScheduleOn,
        
        // Financial breakdown for tracking - matches backend FinancialDetails model
        // Get values from form to ensure we have the current state
        // Extract multiple discount percentages from savingsBreakdown (for duration-based discounts)
        financialDetails: (() => {
          const savingsBreakdown = calculatedValues.savingsBreakdown || [];
          
          // Separate discounts by type
          const hostDiscounts = savingsBreakdown.filter(s => 
            (s.name || '').toLowerCase().includes('host')
          );
          const msDiscounts = savingsBreakdown.filter(s => 
            (s.name || '').toLowerCase().includes('deal') || 
            (s.name || '').toLowerCase().includes('ms')
          );
          
          // Extract all percentages
          const hostDiscountPercentages = hostDiscounts.length > 0 
            ? hostDiscounts.map(d => parseFloat(d.percentage) || 0)
            : [];
          const msDiscountPercentages = msDiscounts.length > 0 
            ? msDiscounts.map(d => parseFloat(d.percentage) || 0)
            : [];
          
          return {
            baseBookingValue: parseFloat(values.bookingValue || form.getFieldValue('bookingValue')) || 0,
            // Single discount percent (for backward compatibility or single discount case)
            hostDiscountPercent: parseFloat(values.hostDiscountPercent || form.getFieldValue('hostDiscountPercent')) || 0,
            // Multiple discount percentages array (for duration-based discounts)
            hostDiscountPercentages: hostDiscountPercentages.length > 0 ? hostDiscountPercentages : null,
            hostDiscountValue: parseFloat(calculatedValues.hostDiscountValue) || 0,
            bookingAfterHostDiscount: parseFloat(calculatedValues.bookingAfterHostDiscount) || 0,
            // Single discount percent (for backward compatibility or single discount case)
            msDiscountPercent: parseFloat(values.msDiscountPercent || form.getFieldValue('msDiscountPercent')) || 0,
            // Multiple discount percentages array (for duration-based discounts)
            msDiscountPercentages: msDiscountPercentages.length > 0 ? msDiscountPercentages : null,
            msDiscountValue: parseFloat(calculatedValues.msDiscountValue) || 0,
            bookingAfterAllDiscounts: parseFloat(calculatedValues.bookingAfterAllDiscounts) || 0,
            taxAmount: parseFloat(values.taxAmount || form.getFieldValue('taxAmount')) || 0,
            finalBookingValue: parseFloat(calculatedValues.finalBookingValue) || 0,
            commissionPercent: parseFloat(values.commissionPercent || form.getFieldValue('commissionPercent')) || 0,
            baseCommission: parseFloat(calculatedValues.baseCommission) || 0,
            paymentToHost: parseFloat(calculatedValues.paymentToHost) || 0,
            ccChargePercent: parseFloat(values.ccChargePercent || form.getFieldValue('ccChargePercent')) || 0,
            ccChargeValue: parseFloat(calculatedValues.ccChargeValue) || 0,
            finalCommissionToMS: parseFloat(calculatedValues.finalCommissionToMS) || 0,
            // NEW: Price adjustment tracking
            isPriceAdjusted: calculatedValues.isPriceAdjusted || false,
            originalCalculatedPrice: calculatedValues.originalCalculatedPrice || 0,
            priceAdjustmentAmount: calculatedValues.priceAdjustmentAmount || 0,
            priceAdjustmentPercent: calculatedValues.priceAdjustmentPercent || 0,
            priceAdjustmentReason: (values.priceAdjustmentReason || form.getFieldValue('priceAdjustmentReason'))?.trim() || null,
            adjustedBy: calculatedValues.isPriceAdjusted ? (localStorage.getItem('adminEmail') || localStorage.getItem('userEmail') || 'admin') : null,
            adjustmentDate: calculatedValues.isPriceAdjusted ? new Date().toISOString() : null
          };
        })()
      };
      
      // Remove undefined fields to avoid sending null values
      Object.keys(bookingData).forEach(key => {
        if (bookingData[key] === undefined) {
          delete bookingData[key];
        }
      });
      
      console.log('Sending complete booking data:', bookingData);
      
      if (editMode && editingId) {
        // Update booking
        await dispatch(updateOfflineBooking(editingId, bookingData));
        
        // Also update payment record if payment details are provided
        if (values.paymentStatus || values.paymentMethod || values.paymentAmount) {
          try {
            console.log('Updating payment record for edited booking:', editingId);
            
            // First, fetch existing payment to get payment ID
            const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
            const existingPaymentResponse = await dispatch(fetchPaymentForOfflineBooking(editingId));
            
            if (existingPaymentResponse && existingPaymentResponse.payload) {
              // Payment exists - update it
              const existingPayment = existingPaymentResponse.payload;
              const paymentId = existingPayment.id || existingPayment._id;
              
              const paymentUpdateData = {
                bookingId: editingId,
                paymentStatus: values.paymentStatus || existingPayment.paymentStatus || 'INITIATED',
                amount: values.paymentAmount !== undefined ? Number(values.paymentAmount) : existingPayment.amount,
                currency: values.currency || existingPayment.currency || 'LKR',
                paymentMethod: values.paymentMethod || existingPayment.paymentMethod || 'BANK_TRANSFER',
                paymentDescription: values.paymentDescription || existingPayment.paymentDescription,
                transactionReference: values.transactionReference || existingPayment.transactionReference || '',
                paymentType: (() => {
                  const method = values.paymentMethod || existingPayment.paymentType;
                  switch (method) {
                    case 'CARD': return 'CARD';
                    case 'CASH': return 'CASH';
                    case 'BANK_TRANSFER': return 'BANK_TRANSFER';
                    case 'CHEQUE': return 'CHEQUE';
                    case 'OTHER': return 'OTHER';
                    default: return 'BANK_TRANSFER';
                  }
                })()
              };
              
              console.log('Updating payment with ID:', paymentId, 'Data:', paymentUpdateData);
              await dispatch(updatePaymentStatusForOfflineBooking(paymentId, paymentUpdateData));
              message.success('Booking and payment updated successfully');
            } else {
              // Payment doesn't exist - create it
              console.log('No existing payment found, creating new payment record');
              
              const paymentData = {
                bookingId: editingId,
                amount: values.paymentAmount !== undefined ? Number(values.paymentAmount) : totalPrice,
                currency: values.currency || 'LKR',
                paymentMethod: values.paymentMethod || 'BANK_TRANSFER',
                paymentStatus: values.paymentStatus || 'INITIATED',
                paymentScheduleOn: values.paymentScheduleOn?.toDate() || new Date(),
                paymentDescription: values.paymentDescription || `Payment for booking ${editingId}`,
                transactionReference: values.transactionReference || '',
                user: clientEmail,
                paymentType: (() => {
                  switch (values.paymentMethod) {
                    case 'CARD': return 'CARD';
                    case 'CASH': return 'CASH';
                    case 'BANK_TRANSFER': return 'BANK_TRANSFER';
                    case 'CHEQUE': return 'CHEQUE';
                    case 'OTHER': return 'OTHER';
                    default: return 'BANK_TRANSFER';
                  }
                })()
              };
              
              await dispatch(createPaymentForOfflineBooking(paymentData));
              message.success('Booking updated and payment record created successfully');
            }
          } catch (paymentError) {
            console.error('Error updating payment:', paymentError);
            message.warning('Booking updated but payment update failed. Please update payment separately.');
          }
        } else {
          message.success('Booking updated successfully');
        }
      } else {
        // Create booking first
        const bookingResponse = await dispatch(createOfflineBooking(bookingData));
        
        console.log('Booking response:', bookingResponse);
        
        // After booking is created successfully, create payment record
        if (bookingResponse && bookingResponse.payload) {
          const createdBooking = bookingResponse.payload;
          const createdBookingId = createdBooking.id || createdBooking.bookingId || createdBooking._id;
          
          console.log('Created booking ID:', createdBookingId);
          console.log('Payment amount:', values.paymentAmount);
          
          if (createdBookingId && (values.paymentAmount !== undefined && values.paymentAmount !== null)) {
            // Validate payment amount range
            const amtNum = Number(values.paymentAmount);
            if (Number.isNaN(amtNum) || amtNum < 0) {
              message.error('Payment amount must be a non-negative number');
              return;
            }
            if (amtNum > totalPrice) {
              message.warning('Payment amount exceeds total price; it will be capped to total');
            }
             // Create payment record - structure matches backend Payment model
             const paymentData = {
              bookingId: createdBookingId,
              amount: (() => {
                const raw = values.paymentAmount;
                if (raw === '' || raw === null || raw === undefined) return totalPrice;
                const num = Number(raw);
                return Number.isNaN(num) ? totalPrice : num;
              })(),
              currency: values.currency || 'LKR',
              paymentMethod: values.paymentMethod || 'BANK_TRANSFER',
              paymentStatus: values.paymentStatus || 'INITIATED',
              paymentScheduleOn: values.paymentScheduleOn?.toDate() || new Date(),
              paymentDescription: values.paymentDescription || `Payment for ${createdBooking.title || 'Offline Booking'}`,
              transactionReference: values.transactionReference || '',
              user: clientEmail && clientEmail.trim() ? clientEmail.trim() : clientName,
              paymentType: (() => {
                switch (values.paymentMethod) {
                  case 'CARD': return 'CARD';
                  case 'CASH': return 'CASH';
                  case 'BANK_TRANSFER': return 'BANK_TRANSFER';
                  case 'CHEQUE': return 'CHEQUE';
                  case 'OTHER': return 'OTHER';
                  default: return 'BANK_TRANSFER';
                }
              })()
            };

            if (values.cancellationAmount) {
              paymentData.cancellationAmount = parseFloat(values.cancellationAmount);
            }

            console.log('Creating payment with data:', paymentData);

            try {
              const paymentResponse = await dispatch(createPaymentForOfflineBooking(paymentData));
              
              console.log('Payment response:', paymentResponse);
              
              if (paymentResponse && paymentResponse.payload) {
                message.success('Offline booking and payment recorded successfully!');
                // Refresh space bookings/calendar to reflect new reservation
                if (selectedSpace) {
                  dispatch(fetchSpaceBookings(selectedSpace));
                }
              } else if (paymentResponse && paymentResponse.error) {
                console.error('Payment creation error:', paymentResponse.error);
                message.warning(`Booking created (${createdBooking.bookingRef || createdBookingId}), but payment recording failed: ${paymentResponse.error}. You can add payment later from booking list.`);
              } else {
                message.warning(`Booking created (${createdBooking.bookingRef || createdBookingId}), but payment recording status unclear. Please verify from booking list.`);
              }
            } catch (paymentError) {
              console.error('Payment creation exception:', paymentError);
              message.warning(`Booking created (${createdBooking.bookingRef || createdBookingId}), but payment recording failed. You can add payment later from booking list.`);
            }
          } else if (!createdBookingId) {
            message.error('Booking created but ID not found. Cannot create payment record.');
          } else {
            message.success(`Offline booking created successfully (${createdBooking.bookingRef || createdBookingId})`);
            // Refresh space bookings/calendar to reflect new reservation
            if (selectedSpace) {
              dispatch(fetchSpaceBookings(selectedSpace));
            }
          }
        } else {
          message.error('Failed to create booking. Please try again.');
        }
      }
      
      handleCancel();
      dispatch(fetchOfflineBookings());
    } catch (err) {
      console.error('Error in proceedWithSubmission:', err);
      message.error(err.message || 'Failed to save booking');
    }
  };
  
  // Handle Space Selection
  const handleSpaceChange = async (spaceId) => {
    if (!spaceId) {
      setSelectedSpace(null);
      dispatch(clearSpaceDetails());
      // Clear discount/commission fields in form
      form.setFieldsValue({
        hostDiscountPercent: undefined,
        msDiscountPercent: undefined,
        commissionPercent: undefined,
        ccChargePercent: undefined,
        taxAmount: undefined
      });
      handleFormValuesChange();
      return;
    }

    // Clear old space details immediately in Redux state
    dispatch(clearSpaceDetails());

    // Clear selected space state to ensure SpaceInfoCard is hidden
    setSelectedSpace(null);

    // Reset charging type and period-specific fields
    form.setFieldsValue({
      selectedChargingType: undefined,
      hourlyBookingDate: undefined,
      bookingStartTime: undefined,
      bookingEndTime: undefined,
      hourlyDuration: undefined,
      dailyStartDate: undefined,
      dailyEndDate: undefined,
      numberOfDays: undefined,
      weeklyStartDate: undefined,
      numberOfWeeks: undefined,
      monthlyStartDate: undefined,
      numberOfMonths: undefined,
      selectedBlock: undefined,
      blockBookingDate: undefined,
      guestCount: 1,
      // Clear financial fields so new space auto-fill applies
      bookingValue: undefined,
      hostDiscountPercent: undefined,
      msDiscountPercent: undefined,
      commissionPercent: undefined,
      ccChargePercent: undefined,
      taxAmount: undefined
    });

    // Clear discount/commission fields in Redux (space details)
    dispatch(resetOfflineBookingForm());

    // Trigger recalculation to reset calculated values immediately
    handleFormValuesChange();

    // Update selected space - this will trigger summary visibility
    setSelectedSpace(spaceId);

    // Fetch new space details and bookings
    try {
      await dispatch(fetchSpaceById(spaceId));
      if (!isMountedRef.current) return;
      await dispatch(fetchSpaceBookings(spaceId));

      // Trigger recalculation after new space details are loaded
      setTimeout(() => {
        handleFormValuesChange();
      }, 100);
    } catch (error) {
      console.error('Error loading space:', error);
      message.error('Failed to load space details');
      setSelectedSpace(null);
    }
  };

  // Refresh spaces list - only when user explicitly clicks refresh button
  const loadSpaces = () => {
    dispatch(fetchAllSpaces(1, 20, '', 'LK', false));
    message.info('Refreshing spaces list...');
  };
  
  // Refresh calendar data for the selected space
  const handleRefreshCalendar = () => {
    if (!selectedSpace) {
      message.warning('Please select a space first');
      return;
    }
    
    // Fetch calendar data for 3 months (similar to ms-web)
    // IMPORTANT: Backend expects DD-MM-YYYY format
    const startMonth = moment().startOf('month').format(CALENDAR_DATE_FORMAT);
    const endMonth = moment().add(3, 'months').format(CALENDAR_DATE_FORMAT);
    
    const payload = {
      startDate: startMonth,
      endDate: endMonth,
    };
    
    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    const headers = token ? {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    } : {
      'Content-Type': 'application/json'
    };
    
    console.log('Fetching calendar for space:', selectedSpace);
    console.log('Date range:', startMonth, 'to', endMonth);
    
    dispatch(
      getCalendarData(selectedSpace, payload, headers, (success, data) => {
        if (success) {
          message.success('Calendar refreshed successfully');
          console.log('Calendar data loaded:', data);
        } else {
          message.error('Failed to refresh calendar data');
        }
      })
    );
  };
  
  // Load calendar data when space is selected or calendar is opened
  useEffect(() => {
    if (selectedSpace && showCalendar) {
      handleRefreshCalendar();
    }
  }, [selectedSpace, showCalendar]);
  
  // Handle Calendar Date Selection
  const handleCalendarSelect = (date) => {
    setSelectedDate(date);
  };
  
  // Handle booking click in calendar view
  const handleBookingClick = (booking) => {
    console.log('Booking clicked:', booking);
    // You can add logic to show booking details or edit booking
  };
  
  // Handle Cancel
  const handleCancel = () => {
    setEditMode(false);
    setEditingId(null);
    setCurrentStep(0);
    setSelectedSpace(null);
    
    // Reset all form fields
    form.resetFields();
    
    // Clear Redux state
    dispatch(resetOfflineBookingForm());
  };
  
  // Navigation
  const handleNext = () => {
    form.validateFields().then(() => {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }).catch((error) => {
      console.log('Validation errors:', error);
      message.warning('Please fill in all required fields before proceeding');
    });
  };
  
  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };
  
  // Calendar Modal
  const showCalendarModal = () => {
    if (!selectedSpace) {
      message.warning('Please select a space first');
      return;
    }
    setShowCalendar(true);
  };
  
  // Error Handling
  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearOfflineBookingError());
    }
  }, [error, dispatch]);
  
  // Form Steps Configuration
  const steps = [
    { title: 'Booking Details', description: 'Space & Date' },
    { title: 'Client Info', description: 'Name & Contact' },
    { title: 'Financial', description: 'Pricing & Discount' },
    { title: 'Payment', description: 'Payment Details' }
  ];
  
  return (
    <div className="offline-booking-container">
      {/* Page Header */}
      <div className="offline-booking-header">
        <h1>Offline Booking Management</h1>
        <div className="header-actions">
          <Button
            type="default"
            icon={<UnorderedListOutlined />}
            onClick={() => history.push('/dashboard/offline-booking-list')}
          >
            View List
          </Button>
          <Button
            type="default"
            icon={<CalendarOutlined />}
            onClick={showCalendarModal}
            disabled={!selectedSpace}
          >
            View Calendar
          </Button>
          {editMode && (
            <Button onClick={handleCancel}>
              Cancel Edit
            </Button>
          )}
        </div>
      </div>
      
      {/* Steps Indicator - Hide in Edit Mode */}
      {!editMode && (
        <Steps 
          current={currentStep} 
          className="offline-booking-steps"
          items={steps}
        />
      )}
      
      {/* Main Content Layout - Full Width Form */}
      <Row gutter={24}>
        {/* Main Form - Full Width */}
        <Col xs={24}>
          {/* Main Form Card */}
          <Card 
            title={editMode ? "Edit Offline Booking" : "Add New Offline Booking"}
            className="offline-booking-form-card"
            loading={loading}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={handleFormValuesChange}
              initialValues={{
                currency: 'LKR',
                paymentStatus: 'COMPLETED',
                numberOfWeeks: 1,
                numberOfMonths: 1
              }}
            >
              {/* Edit Mode - Show All Sections */}
              {editMode ? (
                <>
                  {/* All sections visible in edit mode */}
                  <BookingDetailsForm
                    form={form}
                    spaces={spaces}
                    loadingSpaces={loadingSpaces}
                    spaceDetails={spaceDetails}
                    loadingSpaceDetails={loadingSpaceDetails}
                    selectedSpace={selectedSpace}
                    chargingType={chargingType}
                    availableChargingTypes={availableChargingTypes}
                    availableBlocks={availableBlocks}
                    selectedChargingOption={selectedChargingOption}
                    onSpaceChange={handleSpaceChange}
                    onLoadSpaces={loadSpaces}
                    onChargingTypeChange={handleChargingTypeChange}
                    calculatedValues={calculatedValues}
                    triggerRecalc={handleFormValuesChange}
                  />
                  
                  {spaceDetails && (
                    <SpaceInfoCard 
                      spaceDetails={spaceDetails}
                      availableChargingTypes={availableChargingTypes}
                      selectedChargingOption={selectedChargingOption}
                    />
                  )}
                  
                  <ClientDetailsForm form={form} />
                  
                  <FinancialDetailsForm
                    form={form}
                    calculatedValues={calculatedValues}
                    spaceDetails={spaceDetails}
                    selectedChargingOption={selectedChargingOption}
                    editMode={editMode}
                  />
                  
                  <PaymentDetailsForm 
                    form={form} 
                    calculatedValues={calculatedValues}
                  />
                </>
              ) : (
                <>
                  {/* Step-by-step mode for new bookings */}
                  
                  {/* Step 0: Booking Details */}
                  {currentStep === 0 && (
                    <>
                      <BookingDetailsForm
                        form={form}
                        spaces={memoizedSpaces}
                        loadingSpaces={loadingSpaces}
                        spaceDetails={spaceDetails}
                        loadingSpaceDetails={loadingSpaceDetails}
                        selectedSpace={selectedSpace}
                        chargingType={chargingType}
                        availableChargingTypes={availableChargingTypes}
                        availableBlocks={availableBlocks}
                        selectedChargingOption={selectedChargingOption}
                        onSpaceChange={handleSpaceChange}
                        onLoadSpaces={loadSpaces}
                        onChargingTypeChange={handleChargingTypeChange}
                        calculatedValues={calculatedValues}
                        triggerRecalc={handleFormValuesChange}
                      />
                      
                      {spaceDetails && (
                        <SpaceInfoCard 
                          spaceDetails={spaceDetails}
                          availableChargingTypes={availableChargingTypes}
                          selectedChargingOption={selectedChargingOption}
                        />
                      )}
                    </>
                  )}
                  
                  {/* Step 1: Client Details */}
                  {currentStep === 1 && (
                    <ClientDetailsForm form={form} />
                  )}
                  
                  {/* Step 2: Financial Details */}
                  {currentStep === 2 && (
                    <FinancialDetailsForm
                      form={form}
                      calculatedValues={calculatedValues}
                      spaceDetails={spaceDetails}
                      selectedChargingOption={selectedChargingOption}
                      editMode={false}
                    />
                  )}
                  
                  {/* Step 3: Payment Details */}
                  {currentStep === 3 && (
                    <PaymentDetailsForm 
                      form={form} 
                      calculatedValues={calculatedValues}
                    />
                  )}
                </>
              )}
              
              {/* Form Navigation */}
              <Row gutter={16} style={{ marginTop: 24 }}>
                <Col span={24}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      {!editMode && currentStep > 0 && (
                        <Button onClick={handlePrev}>
                          Previous
                        </Button>
                      )}
                    </Space>
                    
                    <Space>
                      {!editMode && currentStep < steps.length - 1 && (
                        <Button type="primary" onClick={handleNext}>
                          Next
                        </Button>
                      )}
                      
                      {(editMode || currentStep === steps.length - 1) && (
                        <Button type="primary" htmlType="submit" loading={loading}>
                          {editMode ? 'Update Booking' : 'Create Booking'}
                        </Button>
                      )}
                    </Space>
                  </Space>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
      
      {/* Calendar Modal */}
      <Modal
        title={`Booking Calendar - ${spaceDetails?.spaceName || spaceDetails?.name || 'Space'} - All Booking Types`}
        open={showCalendar}
        onCancel={() => setShowCalendar(false)}
        width={1200}
        footer={null}
      >
        <CalendarView
          selectedDate={selectedDate}
          calendarData={calendarData}
          loadingBookings={loadingBookings}
          onCalendarSelect={handleCalendarSelect}
          onRefresh={handleRefreshCalendar}
          onBookingClick={handleBookingClick}
        />
      </Modal>
    </div>
  );
};

export default OfflineBooking;
